import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { UseCompanyWorkspaces } from "../../hooks/company";
import { periodWindowLabelTitle } from "../../helpers/periodWindowLabel";
import { resolveCardWindow } from "../../helpers/cardWindow";
import { monthsForWindow } from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";
import useLaborData, {
  HOURLY_STANDARD_DAY, hoursLabel, isCombinedRow, money, payEntryMode, payEntryValue, pct, rate, rowsOf,
  workspaceIdsOf,
} from "./data";
import PerfPopover from "./PerfPopover";
import PayPopover from "./PayPopover";
import InlineTip from "./InlineTip";
import CardBusy from "../CardScope/CardBusy";
import "./labor.css";

/* A department's roster past this size gets a search box above the list — a
   two- or three-tech team doesn't need one, a twenty-rep sales floor does. */
const EMP_SEARCH_AT = 8;

/**
 * Whoever the roster above is hiding for having no hours yet (data.js's
 * `hiddenEmployees`) — real people in this department the sync has nothing
 * for so far. Collapsed behind a button rather than listed inline: most
 * departments have nobody hidden, and a roster with someone genuinely new
 * shouldn't read as incomplete just because that one row is missing.
 * Picking a name opens PayPopover fresh for them (`onPick`), same as
 * clicking an existing "+ Add rate" cell — from there a monthly rate applies
 * the moment it's typed, with no hours required first.
 */
const HiddenEmployeePicker = ({ hidden, onPick }) => {
  const [open, setOpen] = useState(false);
  if (!hidden.length) return null;
  return (
    <div className="lbr-emp-addwrap">
      <button type="button" className="lbr-emp-add" onClick={() => setOpen((v) => !v)}>
        {open ? "Cancel" : `+ Add employee (${hidden.length} without hours yet)`}
      </button>
      {open && (
        <div className="lbr-emp-addlist">
          {hidden.map((e) => (
            <button
              type="button"
              key={e.userId}
              className="lbr-emp-addopt"
              onClick={(ev) => {
                setOpen(false);
                onPick(e.userId, ev.currentTarget.getBoundingClientRect());
              }}
            >
              {e.name}
              {e.subWorkspace && <em className="lbr-emp-badge">{e.subWorkspace}</em>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
HiddenEmployeePicker.propTypes = {
  hidden: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.string.isRequired,
    name: PropTypes.string,
    subWorkspace: PropTypes.string,
  })).isRequired,
  onPick: PropTypes.func.isRequired,
};

/**
 * Labor cost as a share of revenue, a row per department.
 *
 * The columns are the payroll sheet's, in the sheet's order, so anyone who
 * knows that spreadsheet can read this card without being taught it. What the
 * sheet cannot do is show the answer at a glance: the rail under each
 * performance figure draws the same number against the goal notch, and one
 * department being over its share is legible from across a room.
 *
 * Every figure is computed in ./data.js. Nothing here decides arithmetic.
 */

/* The gate palette the rest of the board already uses, borders included — a
   Gate card tints its frame by status, and this card's headline tile does the
   same so the two read as siblings (see BeatTheYeti's GATE_STYLE). */
const GOOD = {
  ink: "#12a150",
  soft: "rgba(18, 161, 80, 0.1)",
  border: "rgba(18, 161, 80, 0.4)",
  bar: "linear-gradient(90deg,#22c55e,#12a150)",
};
const BAD = {
  ink: "#d92d20",
  soft: "rgba(240, 68, 56, 0.1)",
  border: "rgba(240, 68, 56, 0.4)",
  bar: "linear-gradient(90deg,#F04438,#F79009)",
};
const FLAT = {
  ink: "#667085",
  soft: "rgba(102, 112, 133, 0.1)",
  border: null,
  bar: "#d0d5dd",
};

/* One flag colours both over/under columns — they carry opposite signs in the
   sheet, and a department must never read red in one and green in the next. */
const toneOf = (row) => {
  if (row.deltaAmt == null) return FLAT;
  return row.over ? BAD : GOOD;
};

/* Performance against the goal on one rail: the fill is what the department
   costs, the dashed notch is what it is allowed to cost. */
const Rail = ({ perfPct, goalPct, tone }) => {
  const scale = Math.max(perfPct || 0, goalPct || 0, 1) * 1.25;
  const at = (v) => Math.max(0, Math.min(100, ((Number(v) || 0) / scale) * 100));
  return (
    <span className="lbr-rail" aria-hidden="true">
      <span className="lbr-rail-fill" style={{ transform: `scaleX(${at(perfPct) / 100})`, background: tone.bar }} />
      {goalPct > 0 && <span className="lbr-rail-goal" style={{ left: `${at(goalPct)}%` }} />}
    </span>
  );
};
Rail.propTypes = {
  perfPct: PropTypes.number,
  goalPct: PropTypes.number,
  tone: PropTypes.shape({ bar: PropTypes.string }).isRequired,
};
Rail.defaultProps = { perfPct: 0, goalPct: 0 };

const IcChevron = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * A department's roster, opened under its row. Laid out on the SAME column
 * widths as the outer table (`cols`, passed down from the widths in
 * index.jsx/labor.css) rather than a grid of its own — so Hours and
 * Pay/hour land in the exact lane their totals sit in above, whichever
 * optional columns (Setup's "Show hours", etc.) happen to be switched on.
 * Every column but nm/hours/pay renders an empty cell purely to hold that
 * lane's width; nothing between them carries a per-employee figure.
 *
 * Hours is live wherever HCP (or whatever feeds that workspace) tracks it per
 * person, "—" otherwise. Pay/hour is always typed, one employee at a time —
 * click the figure to open PayPopover (hourly or a monthly salary, converted
 * to its hourly-equivalent), Enter or click away to save. Saving is a no-op
 * with no `editable` (board mid-edit, or a read-only board like a TV wall) —
 * the cell still shows the rate, it just isn't a button.
 */
const EmployeePanel = ({
  row, cols, filter, onFilter, editable, editingCell, onStartEdit,
}) => {
  const emps = row.employees || [];
  const hidden = row.hiddenEmployees || [];
  const q = filter.trim().toLowerCase();
  const shown = q ? emps.filter((e) => e.name.toLowerCase().includes(q)) : emps;
  const gridStyle = { gridTemplateColumns: cols.map((c) => c.width).join(" ") };

  /* Which hint is showing, if any (a name's sub-workspace, or the Pay/hour
     button's own hint) — one at a time for the whole roster, anchored by a
     snapshot rect taken on enter (same rule PerfPopover's own hover follows)
     rather than tracked live, so a target that scrolls out from under a
     lingering pointer can't leave the tip pointing at empty space. */
  const [tip, setTip] = useState(null);
  const tipEnter = (text, e) => {
    if (!text) return;
    setTip({ text, anchor: e.currentTarget.getBoundingClientRect() });
  };
  const tipLeave = () => setTip(null);

  return (
    <div className="lbr-emp-panel">
      {emps.length > EMP_SEARCH_AT && (
        <input
          type="text"
          className="lbr-emp-search"
          placeholder={`Search ${emps.length} employees…`}
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
        />
      )}
      <div className="lbr-emp-head" style={gridStyle}>
        {cols.map((c) => (
          <span key={c.key} className={`lbr-emp-cell lbr-emp-cell-${c.key}`}>
            {c.key === "nm" && "Employee"}
            {c.key === "hours" && "Hours"}
            {c.key === "pay" && "Pay/hour"}
          </span>
        ))}
      </div>
      <div className="lbr-emp-list">
        {shown.length === 0 ? (
          <div className="lbr-emp-none">
            {q ? <>No employees match &quot;{filter}&quot;</> : "Nobody has hours yet this period"}
          </div>
        ) : shown.map((e) => {
          const isEditing = editingCell && editingCell.rowKey === row.key && editingCell.userId === e.userId;
          return (
            <div className="lbr-emp-item" style={gridStyle} key={e.userId}>
              {cols.map((c) => {
                if (c.key === "nm") {
                  return (
                    <span
                      key={c.key}
                      className="lbr-emp-cell lbr-emp-cell-nm lbr-emp-name"
                      onMouseEnter={e.subWorkspace ? (ev) => tipEnter(e.subWorkspace, ev) : undefined}
                      onMouseLeave={e.subWorkspace ? tipLeave : undefined}
                    >
                      <span className="lbr-emp-name-t">{e.name}</span>
                    </span>
                  );
                }
                if (c.key === "hours") {
                  let hoursContent = <span className="lbr-zero">—</span>;
                  if (e.hours != null) {
                    hoursContent = hoursLabel(e.hours);
                  } else if (e.hoursEstimated != null) {
                    /* The raw estimated total (weekdays × 8hr, see
                       monthlyAssumedHours) reads as if it were tracked —
                       spelling out the assumption itself instead makes clear
                       at a glance that nothing here is live. */
                    hoursContent = (
                      <span
                        className="lbr-emp-hours-est"
                        title={`Salaried — no live hours feed. Assumed ${HOURLY_STANDARD_DAY}hrs/weekday, ${hoursLabel(e.hoursEstimated)} hrs this period.`}
                      >
                        {HOURLY_STANDARD_DAY}hrs/day
                      </span>
                    );
                  }
                  return (
                    <span key={c.key} className="lbr-emp-cell lbr-emp-cell-hours lbr-emp-hours">
                      {hoursContent}
                    </span>
                  );
                }
                if (c.key === "pay") {
                  return (
                    <span key={c.key} className="lbr-emp-cell lbr-emp-cell-pay">
                      <button
                        type="button"
                        className={`lbr-emp-pay-btn${editable ? "" : " read"}${isEditing ? " editing" : ""}`}
                        disabled={!editable}
                        onClick={(ev) => {
                          if (!editable || isEditing) return;
                          onStartEdit(e.userId, e.payEntry, ev.currentTarget.getBoundingClientRect());
                        }}
                        onMouseEnter={editable ? (ev) => tipEnter("Click to set this employee's pay", ev) : undefined}
                        onMouseLeave={editable ? tipLeave : undefined}
                      >
                        {e.payPerHour == null ? (
                          <span className="lbr-emp-pay-add">{editable ? "+ Add rate" : "—"}</span>
                        ) : (
                          <>
                            {rate(e.payPerHour)}
                            {payEntryMode(e.payEntry) === "monthly" && <i className="lbr-emp-pay-tag">/hr</i>}
                          </>
                        )}
                      </button>
                    </span>
                  );
                }
                return <span key={c.key} className={`lbr-emp-cell lbr-emp-cell-${c.key}`} />;
              })}
            </div>
          );
        })}
      </div>
      {editable && (
        <HiddenEmployeePicker
          hidden={hidden}
          onPick={(userId, anchor) => onStartEdit(userId, null, anchor)}
        />
      )}
      {tip && <InlineTip anchor={tip.anchor} text={tip.text} />}
    </div>
  );
};
EmployeePanel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  row: PropTypes.object.isRequired,
  cols: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    width: PropTypes.string.isRequired,
  })).isRequired,
  filter: PropTypes.string.isRequired,
  onFilter: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  editingCell: PropTypes.object,
  onStartEdit: PropTypes.func.isRequired,
};
EmployeePanel.defaultProps = { editingCell: null };

const LaborMetrics = ({ settings = {}, title, boardWindow = null, boardCustom = null, blockId = null, saveSettings = null }) => {
  const workspaces = UseCompanyWorkspaces(true) || [];
  /* "Match the dashboard" (default) or a range pinned in Setup — same rule
     every other cross-workspace card follows (see LeaderboardTable, which
     this mirrors) so Labor doesn't grow a second, different meaning for
     "date range". */
  const { window: bucket, custom: resolvedCustom } = resolveCardWindow(settings, {
    window: boardWindow,
    custom: boardCustom,
  });
  const custom = bucket === "custom" ? (resolvedCustom || boardCustom) : null;
  const data = useLaborData(settings, bucket, custom);
  /* Which calendar month(s) a monthly-rate employee's salary is being spread
     across — same resolution useLaborData itself uses (data.js), so the
     Pay/hour column and this card's own edit popover can never disagree on
     what "this month" means. */
  const months = monthsForWindow(bucket === "year" ? "ytd" : bucket, custom);

  const hide = settings.hide || {};
  const shows = (key) => !hide[key];
  const cardTitle = title || settings.title || "Payroll vs Revenue";

  /* A single workspace still names the department by itself; two or more
     combined onto one row (an "Others" catch-all) have no one natural name,
     so a combined row's typed `label` takes over instead. */
  const labelOf = (row) => {
    if (isCombinedRow(row)) return row.label?.trim() || "Others";
    const wsId = workspaceIdsOf(row)[0];
    return workspaces.find((w) => String(w._id) === wsId)?.name || "Department";
  };

  /* One list, in the table's own order, drives three things at once: the
     <thead> below, the colSpan the employee panel opens under, and the
     employee panel's own column widths — so "Hours" and "Pay/hour" land in
     the exact same lane inside the roster as they do in the header above it,
     whichever optional columns (Setup's "Show hours", etc.) happen to be on.
     Widths are the table's own percentages (labor.css) — kept here instead
     of read from the DOM so the two never disagree on a render where one
     hasn't painted yet. */
  const COLS = [
    { key: "nm", width: "13%" },
    ...(shows("hours") ? [{ key: "hours", width: "9%" }] : []),
    { key: "perf", width: "13%" },
    ...(shows("pay") ? [{ key: "pay", width: "10%" }] : []),
    ...(shows("cost") ? [{ key: "cost", width: "12%" }] : []),
    ...(shows("delta") ? [{ key: "delta", width: "10%" }] : []),
    ...(shows("deltaAmt") ? [{ key: "deltaAmt", width: "12%" }] : []),
    ...(shows("goal") ? [{ key: "goal", width: "9%" }] : []),
    ...(shows("goalAmt") ? [{ key: "goalAmt", width: "12%" }] : []),
  ];

  const {
    rows, revenue, revenueLive, totalCost, totalHours, payrollPerfPct, totalGoalPct, totalGoalAmt,
    payrollDeltaAmt, payrollOver,
  } = data;
  const payrollTone = toneOf({ deltaAmt: payrollDeltaAmt, over: payrollOver });

  /* The Performance hover detail — `hover` holds the row it's anchored to and
     stays mounted a beat after the pointer leaves so PerfPopover's exit can
     play instead of the card just vanishing; `hoverOn` is the actual on/off
     switch PerfPopover animates from. Anchored by a snapshot rect taken on
     enter, so a scroll while it's open closes it outright rather than leaving
     it pointing at empty space (same rule ComponentCard's own menu follows). */
  const [hover, setHover] = useState(null);
  const [hoverOn, setHoverOn] = useState(false);
  const hideTimer = useRef(null);

  const perfEnter = (row, e) => {
    clearTimeout(hideTimer.current);
    setHover({ row, anchor: e.currentTarget.getBoundingClientRect() });
    setHoverOn(true);
  };
  const perfLeave = () => {
    setHoverOn(false);
    /* Outlives PerfPopover's own 240ms exit transition (labor.css .lbr-pop)
       so the fade-out finishes before the node is actually removed — shorter
       than that and the popover gets cut off mid-fade instead of settling. */
    hideTimer.current = setTimeout(() => setHover(null), 260);
  };

  useEffect(() => {
    if (!hover) return undefined;
    const close = () => { setHoverOn(false); setHover(null); };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [hover]);
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  /* Which departments are showing their employees, which cell (if any) is
     mid-edit, and the search text narrowing a long roster — all local, all
     reset on remount. `editingCell` carries its own typed value rather than
     writing through on every keystroke, so a rate isn't half-saved if the
     tab closes mid-edit. */
  const [expanded, setExpanded] = useState(() => new Set());
  /* A department's employee panel stays in the DOM once opened, even after
     collapsing it (`expanded` drops it, `mounted` doesn't) — collapsing is a
     CSS transition (labor.css .lbr-emp-animate), and a transition needs the
     element already present to animate FROM; unmounting on close would cut
     it short, and re-mounting on the next open would skip the reveal. */
  const [mounted, setMounted] = useState(() => new Set());
  const toggleExpanded = (key) => setExpanded((cur) => {
    const next = new Set(cur);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      setMounted((m) => (m.has(key) ? m : new Set(m).add(key)));
    }
    return next;
  });
  const [empFilter, setEmpFilter] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { rowKey, userId }
  const [editAnchor, setEditAnchor] = useState(null);
  const [editMode, setEditMode] = useState("hourly");
  const [editVal, setEditVal] = useState("");

  const startEditPay = (rowKey, userId, entry, anchor) => {
    setEditingCell({ rowKey, userId });
    setEditAnchor(anchor);
    setEditMode(payEntryMode(entry));
    const v = payEntryValue(entry);
    setEditVal(v === "" || v == null ? "" : String(v));
  };
  const cancelEditPay = () => {
    setEditingCell(null);
    setEditAnchor(null);
  };
  const commitPay = () => {
    if (!editingCell || !saveSettings || !blockId) { cancelEditPay(); return; }
    const { rowKey, userId } = editingCell;
    const n = Number(editVal);
    const mode = editMode;
    const blank = editVal.trim() === "" || !Number.isFinite(n) || n < 0;
    saveSettings(blockId, (s) => ({
      ...s,
      rows: rowsOf(s).map((r) => {
        if (r.key !== rowKey) return r;
        const pay = { ...(r.employeePay || {}) };
        if (blank) delete pay[userId];
        else if (mode === "monthly") pay[userId] = { mode: "monthly", value: n };
        else pay[userId] = n;
        return { ...r, employeePay: pay };
      }),
    }));
    cancelEditPay();
  };

  const body = () => {
    if (data.loading) return null;
    if (!rows.length) {
      return (
        <div className="lbr-empty">
          <b>No departments yet</b>
          Add one in Edit component — a name, where its hours come from, what it
          costs and the share of revenue it should stay under.
        </div>
      );
    }
    if (revenue <= 0) {
      return (
        <div className="lbr-empty">
          <b>No revenue set</b>
          Every department is measured against the company&apos;s revenue for this
          period. Enter it in Edit component.
        </div>
      );
    }
    /* The employee panel's <td> spans every column so it can open the full
       width of the row — COLS (above) is that same set, so this can never
       drift out of step with what the header actually renders. */
    const colCount = COLS.length;
    /* Whether the Pay/hour cells (and the "+ Add employee" picker inside a
       roster) can actually write anywhere — no saveSettings/blockId means a
       read-only surface (a thumbnail, or the board editor open elsewhere). */
    const editable = !!(saveSettings && blockId);

    return (
      <div className="lbr-scroll">
        <table>
          <thead>
            <tr>
              <th className="nm">Department</th>
              {shows("hours") && <th className="num lbr-hrs">Hours</th>}
              <th className="num lbr-perf">Performance</th>
              {shows("pay") && <th className="num lbr-pay">Pay/hour</th>}
              {shows("cost") && <th className="num lbr-cost">Total cost</th>}
              {shows("delta") && <th className="num lbr-delta">Over/Under</th>}
              {shows("deltaAmt") && <th className="num lbr-deltaamt">Over/Under $</th>}
              {shows("goal") && <th className="num lbr-goal">Goal</th>}
              {shows("goalAmt") && <th className="num lbr-goalamt">Goal $</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const tone = toneOf(row);
              const note = row.over ? "Over its share of revenue" : "Under its share of revenue";
              const hasEmployees = row.employees && row.employees.length > 0;
              /* A department can be fully empty of VISIBLE employees and
                 still have real, hidden ones (a brand-new team with no hours
                 synced at all) — the row still needs to open so "+ Add
                 employee" is reachable, but only when there's something an
                 editable board could actually do about it. */
              const hasHiddenEmployees = row.hiddenEmployees && row.hiddenEmployees.length > 0;
              const canExpand = hasEmployees || (editable && hasHiddenEmployees);
              const isOpen = canExpand && expanded.has(row.key);
              return (
                <React.Fragment key={row.key}>
                <tr>
                  <td className="nm">
                    <span className="lbr-dept">
                      {canExpand && (
                        <button
                          type="button"
                          className={`lbr-chev${isOpen ? " open" : ""}`}
                          onClick={() => toggleExpanded(row.key)}
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? "Hide" : "Show"} ${labelOf(row)}'s employees`}
                        >
                          {IcChevron}
                        </button>
                      )}
                      <i className="lbr-dot" style={{ background: tone.ink }} />
                      <span className="lbr-dept-t">
                        {labelOf(row)}
                        {!row.live && <small>typed in</small>}
                        {hasEmployees && (
                          <small>{row.employees.length} employee{row.employees.length === 1 ? "" : "s"}</small>
                        )}
                      </span>
                    </span>
                  </td>
                  {shows("hours") && <td className="num lbr-hrs">{hoursLabel(row.hours)}</td>}
                  <td
                    className={`num lbr-perf${row.perfPct != null ? " hoverable" : ""}`}
                    onMouseEnter={row.perfPct != null ? (e) => perfEnter(row, e) : undefined}
                    onMouseLeave={row.perfPct != null ? perfLeave : undefined}
                  >
                    <span className="lbr-perf-v">{row.perfPct == null ? "—" : pct(row.perfPct)}</span>
                    {shows("rail") && (
                      <Rail perfPct={row.perfPct || 0} goalPct={row.goalPct} tone={tone} />
                    )}
                  </td>
                  {shows("pay") && (
                    <td className="num lbr-pay">
                      {row.payPerHour == null ? <span className="lbr-zero">—</span> : rate(row.payPerHour)}
                    </td>
                  )}
                  {shows("cost") && <td className="num lbr-cost">{money(row.cost)}</td>}
                  {shows("delta") && (
                    <td className="num lbr-delta" style={{ color: tone.ink }} title={note}>
                      {row.deltaPct == null ? <span className="lbr-zero">—</span> : pct(Math.abs(row.deltaPct))}
                    </td>
                  )}
                  {shows("deltaAmt") && (
                    <td className="num lbr-deltaamt">
                      {row.deltaAmt == null ? (
                        <span className="lbr-zero">—</span>
                      ) : (
                        <span className="lbr-pill" style={{ color: tone.ink, background: tone.soft }} title={note}>
                          {money(Math.abs(row.deltaAmt))}
                        </span>
                      )}
                    </td>
                  )}
                  {shows("goal") && <td className="num lbr-goal">{pct(row.goalPct)}</td>}
                  {shows("goalAmt") && <td className="num lbr-goalamt">{money(row.goalAmt)}</td>}
                </tr>
                {canExpand && mounted.has(row.key) && (
                  <tr className="lbr-emp-row">
                    <td className="lbr-emp-td" colSpan={colCount}>
                      {/* grid-template-rows 0fr↔1fr is the one CSS-only way to
                          transition to/from "auto" height — a plain max-height
                          guess either clips a long roster or leaves dead space
                          under a short one. The panel itself stays mounted
                          once opened (collapsing it to zero rows, not removing
                          it) so re-opening replays the same smooth reveal
                          instead of a re-mount flash. */}
                      <div className={`lbr-emp-animate${isOpen ? " open" : ""}`}>
                        <div className="lbr-emp-animate-in">
                          <EmployeePanel
                            row={row}
                            cols={COLS}
                            filter={empFilter[row.key] || ""}
                            onFilter={(v) => setEmpFilter((cur) => ({ ...cur, [row.key]: v }))}
                            editable={editable}
                            editingCell={editingCell}
                            onStartEdit={(userId, entry, anchor) => startEditPay(row.key, userId, entry, anchor)}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
          {shows("totals") && rows.length > 1 && (
            <tfoot>
              <tr>
                <td className="nm">
                  <span className="lbr-dept">
                    <i className="lbr-dot" style={{ background: payrollTone.ink }} />
                    <span className="lbr-dept-t">Payroll total</span>
                  </span>
                </td>
                {shows("hours") && <td className="num">{hoursLabel(totalHours)}</td>}
                <td className="num" style={{ color: payrollTone.ink }}>
                  {payrollPerfPct == null ? "—" : pct(payrollPerfPct)}
                </td>
                {/* Individual pay rates don't average meaningfully, but the
                    payroll sheet's own blended rate — total cost over total
                    hours — does, so that's what goes here instead of a dash. */}
                {shows("pay") && (
                  <td className="num">
                    {totalHours > 0 ? rate(totalCost / totalHours) : <span className="lbr-zero">—</span>}
                  </td>
                )}
                {shows("cost") && <td className="num">{money(totalCost)}</td>}
                {shows("delta") && (
                  <td className="num" style={{ color: payrollTone.ink }}>
                    {payrollPerfPct == null ? "—" : pct(Math.abs(totalGoalPct - payrollPerfPct))}
                  </td>
                )}
                {shows("deltaAmt") && (
                  <td className="num" style={{ color: payrollTone.ink }}>
                    {payrollDeltaAmt == null ? "—" : money(Math.abs(payrollDeltaAmt))}
                  </td>
                )}
                {shows("goal") && <td className="num">{pct(totalGoalPct)}</td>}
                {shows("goalAmt") && <td className="num">{money(totalGoalAmt)}</td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  return (
    <div className={`gmbw-sales-card lbr${data.refreshing ? " lbr-refreshing" : ""}`}>
      <CardBusy active={data.loading || data.refreshing} cover={data.loading} label={data.loading ? "Loading hours…" : "Updating for date range…"} />
      <div className="gmbw-sales-head">
        <span className="gmbw-sales-title">{cardTitle}</span>
        <span className="gmbw-sales-period">{periodWindowLabelTitle(bucket, custom)}</span>
      </div>
      <div className="gmbw-sales-body">
        {shows("summary") && (
          <div className="lbr-sum">
            <div className="lbr-sum-i">
              <span className="lbr-k">Total revenue{!revenueLive && <small> typed in</small>}</span>
              <span className="lbr-sum-v"><b>{money(revenue)}</b></span>
              <span className="lbr-sub">{periodWindowLabelTitle(bucket, custom)}</span>
            </div>
            <div className="lbr-sum-i">
              <span className="lbr-k">Total cost</span>
              <span className="lbr-sum-v"><b>{money(totalCost)}</b></span>
              <span className="lbr-sub">
                {rows.length} department{rows.length === 1 ? "" : "s"}
                {totalHours > 0 ? ` · ${hoursLabel(totalHours)} hrs` : ""}
              </span>
            </div>
            <div
              className="lbr-sum-i"
              style={payrollTone.border ? { borderColor: payrollTone.border } : undefined}
            >
              <span className="lbr-k">Payroll total perf</span>
              <span className="lbr-sum-v">
                <b style={{ color: payrollTone.ink }}>
                  {payrollPerfPct == null ? "—" : pct(payrollPerfPct)}
                </b>
                {payrollPerfPct != null && totalGoalPct > 0 && (
                  <span className="lbr-pill" style={{ color: payrollTone.ink, background: payrollTone.soft }}>
                    {payrollOver ? "Over" : "Under"}
                  </span>
                )}
              </span>
              {totalGoalPct > 0 ? (
                <Rail perfPct={payrollPerfPct || 0} goalPct={totalGoalPct} tone={payrollTone} />
              ) : (
                <span className="lbr-sub">no goal set</span>
              )}
            </div>
          </div>
        )}
        {body()}
      </div>
      {hover && (
        <PerfPopover
          key={hover.row.key}
          row={hover.row}
          name={labelOf(hover.row)}
          tone={toneOf(hover.row)}
          revenue={revenue}
          anchor={hover.anchor}
          active={hoverOn}
        />
      )}
      {editingCell && (
        <PayPopover
          anchor={editAnchor}
          mode={editMode}
          value={editVal}
          months={months}
          onModeChange={(m) => { setEditMode(m); setEditVal(""); }}
          onValueChange={setEditVal}
          onCommit={commitPay}
          onCancel={cancelEditPay}
        />
      )}
    </div>
  );
};

LaborMetrics.propTypes = {
  /* The card's own settings blob — the same free-form shape every widget stores. */
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  title: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  /* This block's own id on the board (widgetRegistry passes it as render's
     3rd arg) — which block `saveSettings` below should patch. Null off the
     live board (e.g. a thumbnail preview), where editing is disabled anyway. */
  blockId: PropTypes.string,
  /* Homepage's quick-edit save path — lets an employee's Pay/hour cell save
     itself straight to the published layout instead of through the Setup
     drawer. Absent (thumbnail preview, or the whole-board editor is open)
     means the cells render read-only. */
  saveSettings: PropTypes.func,
};

LaborMetrics.defaultProps = {
  settings: {},
  title: undefined,
  boardWindow: null,
  boardCustom: null,
  blockId: null,
  saveSettings: null,
};

export default LaborMetrics;
